import {
  InputLabel,
  Typography,
  TextField,
  Divider,
  Paper,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import clsx from 'clsx';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { DateUtils } from 'react-day-picker';
import { t } from 'ttag';
import { useStyles } from './styles';
import { Button, IconButton, Select, useConfirmation } from '@coolremote/shared';
import { KeyboardArrowDown, Add } from '@material-ui/icons';

import {
  useBacnetScheduleForm,
  useBacnetSchedules,
} from '../../../../../hooks/useBacnetSchedules';
import DeleteIcon from '@icons/DeleteIcon';
import {
  controlTypes,
  createOptionsFromEnum,
  bacnetUnits,
  controlableTypes,
} from '../constants';
import CustomNumericPicker from './CustomNumericPicker';
import DaysDatePicker from '@components/Schedules/components/DaysDatePicker';
import TimePickerRow from '@components/Schedules/components/TimePickerRow';
import ScheduleWrapper from '@components/Schedules/components/ScheduleWrapper';
import ScheduleNameRow from '@components/Schedules/components/ScheduleNameRow';

const AddEditSchedule = (props) => {
  const classes = useStyles();
  const {
    bacnetUnit,
    scheduleData = {},
    onClose,
    uiTemplate,
    paramTemplate,
    isDesktop,
  } = props;

  const confirm = useConfirmation();

  // Use BACnet schedules hook for API operations
  const {
    createSchedule,
    updateSchedule,
    error: scheduleError,
  } = useBacnetSchedules(bacnetUnit?.id);

  // Use the form hook for all form state management
  const {
    // Form data
    scheduleName,
    scheduleDisabled,
    powerOnTime,
    days,
    dates,
    tab,
    scheduleObjects,
    mergedDataPoints,

    dirty,
    errorMsg,

    // Time format utilities
    is12Hours,


    // Actions
    setIsSubmitting,
    setErrorMsg,

    // Object management
    updateScheduleObject,
    removeScheduleObject,
    setScheduleObjects,
    setDirty,

    // Form utilities
    getFormData,
    validateForm,
    resetForm,
    handleFieldChange,
  } = useBacnetScheduleForm(
    bacnetUnit,
    scheduleData,
    uiTemplate,
    paramTemplate
  );

  // Local state for UI interactions
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);
  const [activePickerId, setActivePickerId] = useState(null);
  const activePickerRef = useRef(null);

  const editMode = scheduleData?.id;

  const handleNumericClick = (pointId) => {
    if (activePickerId === pointId) {
      setActivePickerId(null);
    } else {
      setActivePickerId(pointId);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activePickerRef.current && activePickerRef.current.contains(e.target)) {
        return;
      }
      setActivePickerId(null);
    };

    if (activePickerId !== null) {
      window.addEventListener('click', handleClickOutside);
    }

    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, [activePickerId]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const formData = getFormData();
    if (!formData) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Determine if this is an update or create operation
      const isUpdate = scheduleData && scheduleData.id;

      if (isUpdate) {
        // Update existing schedule
        await updateSchedule(scheduleData.id, formData);
      } else {
        // Create new schedule
        await createSchedule(formData);
      }

      // Reset form and close dialog
      resetForm();
      if (onClose) {
        onClose();
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to save schedule';
      setErrorMsg(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (dirty) {
      const result = await confirm({
        title: t`Discard Changes`,
        bodyMessage: t`Are you sure you want to discard changes made on this page?`,
        confirmText: t`Yes`,
        cancelText: t`No`,
        white: true,
        confirmWidth: 120,
        cancelWidth: 120,
      });
      if (result) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Combine error messages
  const combinedErrorMsg = errorMsg || scheduleError;

  // Handle object value change
  const handleObjectValueChange = (objectId, value) => {
    updateScheduleObject(objectId, value);
  };

  const handleSelectCustomRow = (objectId) => {
    if (!objectId) {
      return;
    }

    const dataPoint = mergedDataPoints[objectId] || {};
    const isToggleType =
      dataPoint.controlType === controlTypes.toggle ||
      dataPoint.controlType === controlTypes.onOff;

    setScheduleObjects((prev) => {
      return {
        ...prev,
        [objectId]: {
          id: objectId,
          ...dataPoint,
          value: isToggleType ? false : 0, // Set default value false for toggle/onOff types
        },
      };
    });
    !dirty && setDirty(true);
    setShowSelectionMenu(false);
  };

  // Render control based on type - aligned with control application pattern
  const renderControl = (object) => {
    const { controlType, config = {}, value, id, label } = object;
    const { customEnum, customEnumVisibility } = config;
    const symbol =
      bacnetUnits[config?.userUnits || config?.units]?.symbol || '';
    const selectOptions = createOptionsFromEnum(
      customEnum,
      customEnumVisibility,
      value
    );

    // Shared select control for list, onOff, toggle
    if (
      controlType === controlTypes.list ||
      controlType === controlTypes.onOff ||
      controlType === controlTypes.toggle
    ) {
      return (
        <div
          key={id}
          className={clsx(classes.bodyRow)}
          style={{ padding: '0 10px 0 0' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography className={classes.selectModeStyle}>
            {label ?? config?.name}
          </Typography>
          <div className={classes.rightSection}>
            <Select
              value={value}
              adaptiveWidth
              menuPortal
              onChange={(newValue) =>
                handleObjectValueChange(id, newValue)
              }
              className={classes.selectStyle}
              options={selectOptions}
              arrowIcon={KeyboardArrowDown}
              placeholder='-'
              classes={{
                optionsList: classes.optionsList,
                option: classes.selectOption
              }}
            />
            {actionSection(id)}
          </div>
        </div>
      );
    }

    // Numeric value control
    if (controlType === controlTypes.numericValue) {
      return (
        <div
          key={id}
          ref={activePickerId === id ? activePickerRef : null}
          className={clsx(classes.bodyRow)}
          style={{
            padding: '0',
            height: activePickerId === id ? 'auto' : '43px',
          }}
        >
          <CustomNumericPicker
            pointId={id}
            currentValue={value}
            config={config}
            label={label ?? config?.name}
            handleValueChange={handleObjectValueChange}
            activePickerId={activePickerId}
            handleNumericClick={handleNumericClick}
            symbol={symbol}
            actionSection={actionSection}
          />
        </div>
      );
    }
    return null;
  };

  // Render schedule objects
  const renderScheduleObjects = () => {
    return (
      <>
        {Object.entries(scheduleObjects).map(([, obj]) => {
          return renderControl(obj);
        })}
      </>
    );
  };


  const actionSection = (id) => (
    <div className={classes.rightSection}>
      <Divider
        orientation="vertical"
        flexItem
        style={{ margin: '0 10px', background: '#AAA2AA', width: '1px' }}
      />
      <IconButton
        className={classes.iconButton}
        onClick={() => {
          removeScheduleObject(id);
        }}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  const CustomArrowIcon = (props) => (
    <KeyboardArrowDown {...props} className={classes.arrowIcon} />
  );


  // Object field options for autocomplete
  const objectFieldOptions = mergedDataPoints
    ? Object.entries(mergedDataPoints)
      .filter(
        ([key, point]) =>
          !Object.prototype.hasOwnProperty.call(scheduleObjects, key) &&
          controlableTypes[point?.controlType]
      )
      .map(([key, point]) => ({
        label: point?.label ?? point?.config?.name ?? key,
        value: key,
      }))
    : [];

  // Render selection menu
  const renderSelectionMenu = () => {
    return (
      <div className={classes.bodyRow}>
        <Autocomplete
          options={objectFieldOptions}
          getOptionLabel={(option) => option.label}
          onChange={(_, newValue) => handleSelectCustomRow(newValue?.value)}
          fullWidth
          clearOnBlur
          handleHomeEndKeys
          className={classes.autocompleteWrapper}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={t`Select Control`}
              fullWidth
            />
          )}
          PaperComponent={(props) => (
            <Paper {...props} className={classes.autocompleteMenuPaper} />
          )}
          classes={{
            option: classes.autocompleteOption,
          }}
          popupIcon={<CustomArrowIcon />}
        />
        <Divider
          orientation="vertical"
          flexItem
          style={{ marginRight: '10px', background: '#AAA2AA', width: '1px' }}
        />
        <IconButton onClick={() => setShowSelectionMenu(false)}>
          <DeleteIcon />
        </IconButton>
      </div>
    );
  };

  const handleAddCustomRow = () => {
    setShowSelectionMenu(true);
  };

  const onSetTime = (time) => {
    handleFieldChange('powerOnTime', time);
    !dirty && setDirty(true);
  };

  const handleDayClick = (date, { selected }) => {
    const selectedDates = dates.concat();
    if (selected) {
      const selectedIndex = selectedDates.findIndex((selectedDate) =>
        DateUtils.isSameDay(selectedDate, date)
      );
      selectedDates.splice(selectedIndex, 1);
    } else {
      selectedDates.push(date);
    }
    handleFieldChange('dates', selectedDates);
    !dirty && setDirty(true);
  };

  const today = new Date();
  const aYearFromToday = new Date();
  aYearFromToday.setFullYear(today.getFullYear() + 1);

  return (

    <ScheduleWrapper onXClick={handleCancel} isEditMode={editMode} isDesktop={isDesktop}>

      <ScheduleNameRow
        name={scheduleName}
        onNameChange={(newName) => handleFieldChange('name', newName)}
        disabled={scheduleDisabled}
        onDisabledChange={() => handleFieldChange('disabled', !scheduleDisabled)}
      />

      {/* Days Selection */}
      <DaysDatePicker
        tab={tab}
        onTabChange={(newValue) => handleFieldChange('tab', newValue)}
        days={days}
        dates={dates}
        onAddRemoveDay={(day) => {
          const newDays = days.includes(day)
            ? days.filter((d) => d !== day)
            : [...days, day];
          handleFieldChange('days', newDays);
        }}
        onDayClick={handleDayClick}
      />

      {/* Power On Time */}
      <TimePickerRow
        id={'time-picker'}
        currentValue={powerOnTime}
        label={t`Schedule Time`}
        handleTimeChange={onSetTime}
        is12Hours={is12Hours}
      />

      <Typography className={clsx(classes.selectModeStyle, classes.selectModeLabel, classes.actionsLabel)}>
        {t`Actions`}
      </Typography>

      {/* Render Schedule Objects */}
      {renderScheduleObjects()}
      {!!showSelectionMenu && renderSelectionMenu()}
      <div
        className={classes.newRowContainer}
        onClick={handleAddCustomRow}
      >
        <Add className={classes.addIcon} />
        <Typography className={classes.addText}>{t`Add Controls`}</Typography>
      </div>
      {/* Error Message */}
      {combinedErrorMsg && (
        <InputLabel className={classes.errorLabelStyle}>
          {combinedErrorMsg}
        </InputLabel>
      )}

      <div className={classes.actionsContainer}>
        <Button
          disableElevation
          disableRipple
          variant="text"
          onClick={handleCancel}
          className={classes.cancelButton}
        >
          {t`Cancel`}
        </Button>
        <Button
          disableElevation
          disableRipple
          type="submit"
          variant="contained"
          onClick={handleSubmit}
          disabled={!dirty}
          className={classes.redBtn}
        >
          {t`Save`}
        </Button>
      </div>
    </ScheduleWrapper>
  );
};
export default AddEditSchedule;
